import { useMemo, useState } from 'react';
import Search from '@carbon/react/es/components/Search/Search';
import {
  PlatformRole,
  platformRoleAtLeast,
  type CatalogPart,
  type HardwareCategory,
} from '@ddp/shared';

import { useAuthStore } from '../../auth/store.js';
import { useT, type MessageKey } from '../../i18n/index.js';
import { useCatalog } from './use-catalog.js';
import { CatalogMediaEditor } from './CatalogMediaEditor.js';

/** Libellé i18n d'une catégorie de pièce. */
export function categoryKey(cat: HardwareCategory): MessageKey {
  return `hw.cat.${cat}` as MessageKey;
}

/** Quelques specs marquantes d'une pièce, sous forme de puces `clé valeur`. */
function SpecChips({ specs }: { specs: CatalogPart['specs'] }) {
  const entries = Object.entries(specs).slice(0, 4);
  if (entries.length === 0) return null;
  return (
    <div className="ddp-cat__specs">
      {entries.map(([k, v]) => (
        <span key={k} className="ddp-cat__spec">
          {k} <b>{String(v)}</b>
        </span>
      ))}
    </div>
  );
}

/**
 * Catalogue de pièces (ADR-0030) : navigation en lecture seule du référentiel **global organisé**.
 * Filtre par catégorie (chips) et recherche texte, côté client — le catalogue est modeste. La
 * curation d'écriture (ajout / dépréciation de références) est réservée au `SUPERADMIN`, hors de
 * cette UI (rendue par l'API + le seed).
 */
export function CatalogBrowser() {
  const t = useT();
  const { parts, loading, refresh } = useCatalog();
  const [cat, setCat] = useState<HardwareCategory | 'ALL'>('ALL');
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState<CatalogPart | null>(null);

  // Curation de l'illustration (ADR-0032) : réservée au SUPERADMIN.
  const platformRole = useAuthStore((s) => s.currentUser?.platformRole ?? null);
  const canCurate =
    platformRole !== null && platformRoleAtLeast(platformRole, PlatformRole.SUPERADMIN);

  // Catégories réellement présentes, avec leur décompte, pour ne pas offrir des filtres vides.
  const categories = useMemo(() => {
    const counts = new Map<HardwareCategory, number>();
    for (const p of parts) counts.set(p.category, (counts.get(p.category) ?? 0) + 1);
    return [...counts.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [parts]);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return parts.filter(
      (p) =>
        (cat === 'ALL' || p.category === cat) &&
        (!needle || `${p.brand} ${p.model} ${p.variant}`.toLowerCase().includes(needle)),
    );
  }, [parts, cat, q]);

  return (
    <div className="ddp-cat">
      <aside className="ddp-cat__filters">
        <Search
          id="catalog-search"
          size="sm"
          labelText={t('hw.catalog.searchLabel')}
          placeholder={t('hw.catalog.searchPlaceholder')}
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <div className="ddp-cat__chips">
          <button
            type="button"
            className="ddp-cat__chip"
            aria-pressed={cat === 'ALL'}
            onClick={() => setCat('ALL')}
          >
            <span>{t('hw.catalog.all')}</span>
            <span className="ddp-cat__n">{parts.length}</span>
          </button>
          {categories.map(([c, n]) => (
            <button
              key={c}
              type="button"
              className="ddp-cat__chip"
              aria-pressed={cat === c}
              onClick={() => setCat(c)}
            >
              <span>{t(categoryKey(c))}</span>
              <span className="ddp-cat__n">{n}</span>
            </button>
          ))}
        </div>
        <p className="ddp-cat__curate">{t('hw.catalog.curated')}</p>
      </aside>

      <div className="ddp-cat__grid">
        {loading ? (
          <p className="ddp-cat__muted">{t('common.loading')}</p>
        ) : shown.length === 0 ? (
          <p className="ddp-cat__muted">{t('hw.catalog.empty')}</p>
        ) : (
          shown.map((p) => (
            <article key={p.id} className="ddp-cat__card">
              {(p.mediaUrl || canCurate) && (
                <div className="ddp-cat__media">
                  {p.mediaUrl ? (
                    <img src={p.mediaUrl} alt={`${p.brand} ${p.model}`} loading="lazy" />
                  ) : (
                    <span className="ddp-cat__media-empty" aria-hidden="true" />
                  )}
                  {canCurate && (
                    <button
                      type="button"
                      className="ddp-cat__media-edit"
                      onClick={() => setEditing(p)}
                    >
                      {t(p.mediaUrl ? 'hw.media.edit' : 'hw.media.add')}
                    </button>
                  )}
                </div>
              )}
              <div className="ddp-cat__cat">{t(categoryKey(p.category))}</div>
              <h4 className="ddp-cat__name">
                {p.brand} {p.model}
              </h4>
              {p.variant && <div className="ddp-cat__variant">{p.variant}</div>}
              <SpecChips specs={p.specs} />
              <div className="ddp-cat__foot">
                <span
                  className={`ddp-cat__status ddp-cat__status--${p.status === 'ACTIVE' ? 'active' : 'dep'}`}
                >
                  {t(p.status === 'ACTIVE' ? 'hw.status.active' : 'hw.status.deprecated')}
                </span>
              </div>
            </article>
          ))
        )}
      </div>

      {editing && (
        <CatalogMediaEditor
          part={editing}
          onClose={() => setEditing(null)}
          onDone={() => {
            setEditing(null);
            void refresh();
          }}
        />
      )}
    </div>
  );
}
